import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarDays,
  Briefcase,
  FileSpreadsheet,
  Activity,
  Settings,
  Users,
  Plus,
  ChevronRight,
  Sparkles,
  PlugZap,
} from 'lucide-react';
import { cn } from '@shared';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { WorkspaceSelector } from '@/components/layout/WorkspaceSelector';
import { Image } from '@/components/ui/Image';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { toast } from 'sonner';

const mainItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/daysheetly', icon: CalendarDays, label: 'Day Sheetly' },
  { to: '/projects', icon: Briefcase, label: 'Projects' },
  { to: '/reports', icon: FileSpreadsheet, label: 'Reports' },
  { to: '/activities/review', icon: Activity, label: 'Activities' },
];

function NavSection({
  title,
  items,
}: {
  title: string;
  items: { to: string; icon: React.ElementType; label: string }[];
}) {
  return (
    <div className="px-3 py-2">
      <div className="mb-1 flex items-center justify-between px-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-sidebar-muted">
          {title}
        </span>
        <Plus className="h-3 w-3 text-sidebar-muted" />
      </div>
      <div className="space-y-0.5">
        {items.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={label}
            to={to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive && to.startsWith('/')
                  ? 'bg-sidebar-active/20 text-white'
                  : 'text-sidebar-muted hover:bg-white/10 hover:text-white',
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function SidebarContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const {
    workspaces,
    activeWorkspaceId,
    switchWorkspace,
  } = useWorkspace();

  const activeId =
    activeWorkspaceId && workspaces.some((workspace) => workspace.id === activeWorkspaceId)
      ? activeWorkspaceId
      : workspaces[0]?.id ?? null;

  const navigationItems = activeId
    ? [
        ...mainItems.slice(0, 4),
        { to: '/workspace/' + activeId + '/settings/members', icon: Users, label: 'Members' },
        mainItems[4],
      ]
    : mainItems;

  const handleSwitch = async (workspaceId: string) => {
    try {
      await switchWorkspace(workspaceId);

      // Members is the only workspace-specific route. Keep its URL aligned
      // with the newly active workspace instead of leaving a stale id behind.
      if (location.pathname.startsWith('/workspace/')) {
        navigate('/workspace/' + workspaceId + '/settings/members', { replace: true });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not switch workspace');
    }
  };

  return (
    <>
      <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-6">
        <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-white">
          <Image src="/logo-icon.png" alt="Day Sheetly" width={36} height={36} priority />
        </div>
        <span className="text-lg font-semibold tracking-tight text-sidebar-foreground">
          Day Sheetly
        </span>
      </div>

      <div className="p-3">
        <WorkspaceSelector
          workspaces={workspaces}
          activeWorkspaceId={activeId}
          onSwitch={handleSwitch}
        />
      </div>

      <nav aria-label="Main navigation" className="flex-1 space-y-1 overflow-y-auto py-2">
        <NavSection title="Main" items={navigationItems} />
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="settings-trigger"
              aria-label="Settings"
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-muted transition-colors hover:bg-white/10 hover:text-white data-[state=open]:bg-sidebar-active/20 data-[state=open]:text-white"
            >
              <Settings className="h-4 w-4" />
              Settings
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            sideOffset={8}
            className="w-[232px] rounded-xl p-3"
          >
            <div className="border-b pb-2.5">
              <h2 className="text-sm font-semibold">Settings</h2>
            </div>
            <button
              type="button"
              data-testid="settings-subscription-entry"
              onClick={() => {
                setSettingsOpen(false);
                navigate('/settings/subscription');
              }}
              className="mt-2.5 flex w-full items-center gap-2.5 rounded-lg bg-primary/10 px-2.5 py-2.5 text-left transition-colors hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Subscription</span>
                <span className="block text-xs text-muted-foreground">Plan and billing</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
            <button
              type="button"
              data-testid="settings-mcp-entry"
              onClick={() => {
                setSettingsOpen(false);
                navigate('/settings/integrations/mcp');
              }}
              className="mt-2.5 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <PlugZap className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">AI Agents &amp; MCP</span>
                <span className="block text-xs text-muted-foreground">Connect any agent</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}

export function Sidebar() {
  return (
    <aside
      className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col lg:flex"
      style={{ backgroundColor: 'hsl(var(--sidebar))' }}
    >
      <SidebarContent />
    </aside>
  );
}
